import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const REF_ATTR = 'data-ref';

class FlooringCalculator extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this._state = {
      boxSizeSqm: parseFloat(this.dataset.boxSizeSqm) || 0,
      pricePerSqmCents: parseInt(this.dataset.pricePerSqmCents, 10) || 0,
      variantPriceCents: parseInt(this.dataset.variantPriceCents, 10) || 0,
      weightPerBox: parseFloat(this.dataset.weightPerBox) || 0,
      boxesPerPallet: parseInt(this.dataset.boxesPerPallet, 10) || 0,
      currency: this.dataset.currency || 'USD',
      moneyFormat: this.dataset.moneyFormat || '${{amount}}',
      weightUnit: this.dataset.weightUnit || 'kg',
      sqmUnit: this.dataset.sqmUnit || 'm²',
      wastagePct: 0,
      sqm: 0,
      boxes: 1,
      lastEdited: null,
    };

    this._refs = {};
    this.querySelectorAll(`[${REF_ATTR}]`).forEach((el) => {
      const name = el.getAttribute(REF_ATTR);
      if (!name) return;
      this._refs[name] = el;
    });

    this._bindEvents();
    this._syncFromInputs();
    this._recompute();

    const section = this.closest('.shopify-section, dialog') || document;
    this._onVariantUpdate = (event) => this._handleVariantUpdate(event);
    section.addEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section, dialog') || document;
    section.removeEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
  }

  _bindEvents() {
    const { sqmInput, sqmMinus, sqmPlus, boxesInput, boxesMinus, boxesPlus } = this._refs;

    if (sqmInput) {
      sqmInput.addEventListener('input', () => this._onSqmChange());
      sqmInput.addEventListener('blur', () => this._onSqmChange(true));
    }
    sqmMinus?.addEventListener('click', () => this._stepSqm(-1));
    sqmPlus?.addEventListener('click', () => this._stepSqm(+1));

    if (boxesInput) {
      boxesInput.addEventListener('input', () => this._onBoxesChange());
      boxesInput.addEventListener('blur', () => this._onBoxesChange(true));
    }
    boxesMinus?.addEventListener('click', () => this._stepBoxes(-1));
    boxesPlus?.addEventListener('click', () => this._stepBoxes(+1));

    this.querySelectorAll('input[type="radio"][data-ref="wastageInput"]').forEach((input) => {
      input.addEventListener('change', () => this._onWastageChange());
    });
  }

  _syncFromInputs() {
    const { sqmInput, boxesInput } = this._refs;
    this._state.sqm = Math.max(0, parseFloat(sqmInput?.value) || 0);
    this._state.boxes = Math.max(0, Math.floor(parseFloat(boxesInput?.value) || 0));
    const checked = this.querySelector('input[type="radio"][data-ref="wastageInput"]:checked');
    this._state.wastagePct = parseFloat(checked?.value) || 0;
  }

  _onSqmChange(blurred = false) {
    const raw = this._refs.sqmInput?.value ?? '';
    const parsed = parseFloat(raw);
    this._state.sqm = isFinite(parsed) && parsed > 0 ? parsed : 0;
    this._state.lastEdited = 'sqm';
    this._state.boxes = this._boxesFromSqm(this._state.sqm, this._state.wastagePct);
    if (this._refs.boxesInput) this._refs.boxesInput.value = String(this._state.boxes);
    if (blurred && this._refs.sqmInput) {
      this._refs.sqmInput.value = this._state.sqm.toFixed(2);
    }
    this._recompute();
  }

  _onBoxesChange(blurred = false) {
    const raw = this._refs.boxesInput?.value ?? '';
    const parsed = parseInt(raw, 10);
    this._state.boxes = isFinite(parsed) && parsed > 0 ? parsed : 0;
    this._state.lastEdited = 'boxes';
    if (blurred && this._refs.boxesInput) {
      this._refs.boxesInput.value = String(this._state.boxes);
    }
    this._syncSqmFromBoxes();
    this._recompute();
  }

  _syncSqmFromBoxes() {
    if (!this._state.boxSizeSqm) return;
    this._state.sqm = this._state.boxes * this._state.boxSizeSqm;
    if (this._refs.sqmInput) {
      this._refs.sqmInput.value = this._state.sqm.toFixed(2);
    }
  }

  _onWastageChange() {
    const checked = this.querySelector('input[type="radio"][data-ref="wastageInput"]:checked');
    this._state.wastagePct = parseFloat(checked?.value) || 0;
    if (this._state.sqm > 0) {
      this._state.boxes = this._boxesFromSqm(this._state.sqm, this._state.wastagePct);
      if (this._refs.boxesInput) this._refs.boxesInput.value = String(this._state.boxes);
      this._state.lastEdited = 'sqm';
    }
    this._recompute();
  }

  _stepSqm(direction) {
    const step = 1;
    const next = Math.max(0, (this._state.sqm || 0) + direction * step);
    if (this._refs.sqmInput) this._refs.sqmInput.value = next.toFixed(2);
    this._onSqmChange();
  }

  _stepBoxes(direction) {
    const next = Math.max(0, (this._state.boxes || 0) + direction);
    if (this._refs.boxesInput) this._refs.boxesInput.value = String(next);
    this._onBoxesChange();
  }

  _boxesFromSqm(sqm, wastagePct) {
    if (!sqm || sqm <= 0 || !this._state.boxSizeSqm) return 0;
    const withWaste = sqm * (1 + wastagePct / 100);
    return Math.max(0, Math.ceil(withWaste / this._state.boxSizeSqm));
  }

  _recompute() {
    const s = this._state;
    const purchaseSqm = s.boxes * s.boxSizeSqm;
    const totalWeight = s.boxes * s.weightPerBox;
    const totalCents = s.boxes * s.variantPriceCents;

    this._setText('purchaseQtyOut', `${this._formatNumber(purchaseSqm, 2)} ${s.sqmUnit}`);
    this._setText('totalWeightOut', `${this._formatNumber(totalWeight, 2)} ${s.weightUnit}`);

    if (this._refs.totalPalletsOut && s.boxesPerPallet > 0) {
      const pallets = Math.ceil(s.boxes / s.boxesPerPallet);
      this._setText('totalPalletsOut', String(pallets));
    }

    this._setText('totalOut', formatMoney(totalCents, s.moneyFormat, s.currency));

    if (this.dataset.requestQuote === 'true') {
      return;
    }

    const quantityInput = this.querySelector('input[data-ref="quantityInput"]');
    if (quantityInput) {
      const q = s.boxes > 0 ? s.boxes : 1;
      quantityInput.value = String(q);
    }

    this._syncAddToCartState();
  }

  _syncAddToCartState() {
    const container = this.querySelector('add-to-cart-component');
    if (!container?.refs?.addToCartButton) return;
    const variantOk = this.dataset.variantCanAdd === 'true';
    if (!variantOk) {
      container.disable();
      return;
    }
    if (this._state.boxes > 0) {
      container.enable();
    } else {
      container.disable();
    }
  }

  _setText(ref, text) {
    const el = this._refs[ref];
    if (el) el.textContent = text;
  }

  _formatNumber(value, decimals) {
    if (!isFinite(value)) return (0).toFixed(decimals);
    return value.toFixed(decimals);
  }

  _handleVariantUpdate(event) {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.productId && this.dataset.productId && String(data.productId) !== String(this.dataset.productId)) {
      if (!data.newProduct) return;
    }

    const resource = detail.resource;
    if (resource && typeof resource.price !== 'undefined') {
      this._state.variantPriceCents = parseInt(resource.price, 10) || 0;
    }

    if (resource?.id) {
      const variantIdInput = this.querySelector('input[name="id"]');
      if (variantIdInput) variantIdInput.value = resource.id;
    }

    if (data.html) {
      const newEl = data.html.querySelector(
        `flooring-calculator[data-block-id="${this.dataset.blockId}"]`
      );
      if (newEl) {
        const syncedAttrs = [
          'data-request-quote',
          'data-variant-can-add',
          'data-variant-price-cents',
          'data-price-per-sqm-cents',
          'data-weight-per-box',
          'data-box-size-sqm',
          'data-boxes-per-pallet',
        ];
        syncedAttrs.forEach((attr) => {
          const v = newEl.getAttribute(attr);
          if (v != null) this.setAttribute(attr, v);
        });
        this._state.variantPriceCents = parseInt(this.dataset.variantPriceCents, 10) || 0;
        this._state.pricePerSqmCents = parseInt(this.dataset.pricePerSqmCents, 10) || 0;
        this._state.weightPerBox = parseFloat(this.dataset.weightPerBox) || 0;
        this._state.boxSizeSqm = parseFloat(this.dataset.boxSizeSqm) || 0;
        this._state.boxesPerPallet = parseInt(this.dataset.boxesPerPallet, 10) || 0;

        const newBoxSizeOut = newEl.querySelector('[data-ref="boxSizeOut"]');
        if (newBoxSizeOut && this._refs.boxSizeOut) {
          this._refs.boxSizeOut.textContent = newBoxSizeOut.textContent;
        }
        const newPricePerSqmOut = newEl.querySelector('[data-ref="pricePerSqmOut"]');
        if (newPricePerSqmOut && this._refs.pricePerSqmOut) {
          this._refs.pricePerSqmOut.textContent = newPricePerSqmOut.textContent.trim();
        }
        const newWeightPerBoxOut = newEl.querySelector('[data-ref="weightPerBoxOut"]');
        if (newWeightPerBoxOut && this._refs.weightPerBoxOut) {
          this._refs.weightPerBoxOut.textContent = newWeightPerBoxOut.textContent.trim();
        }

        if (this.dataset.requestQuote !== 'true') {
          const newAtc = newEl.querySelector('add-to-cart-component');
          const curAtc = this.querySelector('add-to-cart-component');
          if (newAtc && curAtc) {
            if (newAtc.dataset.productVariantMedia) {
              curAtc.dataset.productVariantMedia = newAtc.dataset.productVariantMedia;
            }
            const newBtn = newAtc.querySelector('[ref="addToCartButton"]');
            const curBtn = curAtc.querySelector('[ref="addToCartButton"]');
            if (newBtn && curBtn) {
              curBtn.disabled = newBtn.disabled;
              const nFrom = newBtn.querySelector('.add-to-cart-text__content');
              const cFrom = curBtn.querySelector('.add-to-cart-text__content');
              if (nFrom && cFrom) {
                cFrom.innerHTML = nFrom.innerHTML;
              }
            }
          }

          const newAccel = newEl.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
          const curAccel = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
          if (newAccel && curAccel) {
            if (newAccel.hasAttribute('hidden')) {
              curAccel.setAttribute('hidden', '');
            } else {
              curAccel.removeAttribute('hidden');
            }
          }
        }
      }
    }

    if (this._state.lastEdited !== 'boxes') {
      this._state.boxes = this._boxesFromSqm(this._state.sqm, this._state.wastagePct);
      if (this._refs.boxesInput) this._refs.boxesInput.value = String(this._state.boxes);
    }

    this._recompute();
  }
}

if (!customElements.get('flooring-calculator')) {
  customElements.define('flooring-calculator', FlooringCalculator);
}
